import React, { FC, useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Stack,
  Snackbar,
  Alert,
  Chip,
  Link as MuiLink,
  Tabs,
  Tab,
  MenuItem,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  PlayCircleOutline,
  StopCircleOutlined,
  OpenInNewOutlined,
  FiberManualRecord,
  CheckCircleOutline,
} from '@mui/icons-material';
import moment from 'moment';
import { api } from '../../helpers/apiUtils';
import {
  Abschnitt,
  GefahrBestaetigung,
  NurAdmin,
  useIstAdmin,
} from '../../components/marketingForm';
import Schedule from './Schedule';
import Replays from './Replays';
import Stats from './Stats';
import AdVideos from './AdVideos';
import Einblendungen, { LaufendeEinblendung } from './Einblendungen';
import { BEGRIFF_LIVE, BESTAETIGEN_BIS, TECHNIK_KONTAKT } from './texteLive';

/**
 * Live-Admin. Paket 2 (Persona-Runde 0, 28.10.2026): der Reiter „Heute“ ist das Sendepult für den Moderator —
 * oben Status, „Live gehen“/„Sendung beenden“ und das Signal aus dem Studio, darunter die Einblendungen, dann der
 * Rest. Technik (Sendungs-Nr., Mailchimp-Liste, Notfall-Fernbedienung) steht nur im Admin-Bereich.
 * Der CTA-Reiter ist aus der Navigation genommen (Till Q2, ersetzt durch die Einblendungen); `CtaControl.tsx`
 * bleibt als Datei liegen.
 */

interface UnbestaetigterTermin {
  date: string;
  time: string;
  iso: string;
  source: string;
  is_today: boolean;
  broadcast_source: 'live' | 'recorded';
  /** Eingetragene Themen des Sendetags (routes/live.ts admin/status); fehlt bei älterem Server. */
  themen_anzahl?: number;
}

interface LiveStatus {
  is_public: boolean;
  state?: 'live' | 'auto-roll' | 'pre-show' | 'post-show' | 'offline';
  phase?: 'live' | 'auto-roll' | null;
  broadcast_kind?: string;
  hls_url: string | null;
  session_id: number | null;
  started_at: string | null;
  ended_at: string | null;
  auto_roll_started_at?: string | null;
  ad_video_id?: number | null;
  // Nur während `state='live'`: das Werbevideo, das nach „Sendung beenden“ läuft (routes/live.ts adVideoPreload).
  ad_video_preload?: { id: number; title: string | null } | null;
  manual_cta_visible: boolean;
  cta: { label: string; url: string } | null;
  next_show: {
    date: string;
    time: string;
    iso: string;
    source: 'default' | 'reschedule' | 'extra' | 'test';
    label: string | null;
    is_today: boolean;
    is_test: boolean;
  } | null;
  next_show_text?: string;
  is_test_mode?: boolean;
  server_time: string;
  // Nur von /api/live/admin/status (ungegatet, frisch gelesen) — siehe routes/live.ts.
  admin_diag?: {
    is_public: boolean;
    current_session: {
      id: number;
      source_type: string | null;
      show_action: string | null;
      started_at: string | null;
      ended_at: string | null;
    } | null;
    provider_active_since?: string | null;
    provider_idle_at?: string | null;
    provider_pushing: boolean;
    inkonsistent: boolean;
    test_gate_aktiv: boolean;
    // D7 (P3): Test-Vorschau mit Token — nur ueber diese adminAuth-Antwort, nie im Bundle.
    test_vorschau_url?: string | null;
    // Paket 2 (A5): unbestaetigte Termine heute/morgen; null = konnte nicht gelesen werden.
    unbestaetigt?: UnbestaetigterTermin[] | null;
  };
}

// GET /api/live/admin/preflight (P3/D5): je heutigem Termin, ob er starten wird — und wenn nicht, warum.
type PreflightStufe = 'ok' | 'warn' | 'fehler';
type SlotZustand = 'test' | 'abgesagt' | 'gestartet' | 'vor_frist' | 'ausgefallen' | 'nach_fenster';
interface PreflightSlot {
  iso: string;
  time: string;
  source: string;
  broadcast_source: 'live' | 'recorded';
  status: 'pending' | 'confirmed' | 'cancelled' | null;
  cancel_reason: string | null;
  zustand: SlotZustand;
  session_id: number | null;
  minuten_seit_start: number;
  punkte: Array<{ stufe: PreflightStufe; text: string }>;
}
interface Preflight {
  heute: string;
  slots: PreflightSlot[];
  sendeplatz: { is_public: boolean; current_session_id: number | null; provider_pushing: boolean };
}

/** Was „Jetzt bestätigen“ braucht (POST /admin/schedule/confirm nimmt Tag + Termin-Art). */
interface BestaetigenZiel {
  date: string;
  source: string;
  time: string;
  is_today: boolean;
  broadcast_source: 'live' | 'recorded';
  /** undefined = unbekannt (dann keine Themen-Warnung). */
  themen_anzahl?: number;
}

/** Was „Absagen …“ auf „Heute“ braucht — dieselbe Route wie im Termin-Dialog (POST /admin/schedule/cancel). */
interface AbsagenZiel {
  date: string;
  source: string;
  time: string;
  is_today: boolean;
  broadcast_source: 'live' | 'recorded';
  /** Für den Satz zur 08:30-Mail: unbestätigt (pending) oder schon bestätigt. */
  status: 'pending' | 'confirmed';
}

/** Absagen geht nur für echte Termine (Zusatz/Verschoben), wie im Termin-Dialog (Schedule.tsx istGated). */
const istAbsagbareQuelle = (source: string) => source === 'extra' || source === 'reschedule';

const PREFLIGHT_POLL_MS = 30_000;
// Ein haengender Preflight darf weder GO LIVE noch die uebrigen Knoepfe blockieren (Review P3-UI #1).
const PREFLIGHT_TIMEOUT_MS = 5000;
// Aelter als zwei Poll-Runden → die Karte sagt, dass sie nicht frisch ist (Review P3-UI #2).
const PREFLIGHT_VERALTET_MS = 2 * PREFLIGHT_POLL_MS + PREFLIGHT_TIMEOUT_MS;
// D9: so viele Poll-Intervalle ohne Antwort, dann gilt der angezeigte Zustand als veraltet.
const VERALTET_NACH_POLLS = 3;

const ZUSTAND_TEXT: Record<
  SlotZustand,
  { label: string; color: 'default' | 'success' | 'error' | 'warning' | 'info' }
> = {
  vor_frist: { label: 'geplant', color: 'info' },
  gestartet: { label: 'gestartet', color: 'success' },
  ausgefallen: { label: 'nicht gestartet', color: 'error' },
  nach_fenster: { label: 'nicht gestartet', color: 'error' },
  abgesagt: { label: 'abgesagt', color: 'default' },
  test: { label: 'Test', color: 'default' },
};

const STUFE_FARBE: Record<PreflightStufe, string> = {
  ok: 'success.main',
  warn: 'warning.main',
  fehler: 'error.main',
};

// Fehlercodes der Live-Admin-Routen, die keinen eigenen Klartext in `details` mitbringen.
const FEHLER_TEXTE: Record<string, string> = {
  sendeplatz_belegt: 'Es läuft bereits eine Sendung.',
  termin_abgesagt: 'Der Termin ist abgesagt.',
  termin_geaendert: 'Der Termin wurde inzwischen geändert — bitte die Ansicht prüfen.',
  kein_aufzeichnungs_termin: 'Für diese Uhrzeit ist heute keine Aufzeichnung geplant.',
  termin_nicht_heute: 'Nachstarten geht nur für heutige Termine.',
  start_now_failed: 'Start fehlgeschlagen — bitte erneut versuchen.',
  not_found: 'Diesen Termin gibt es nicht (mehr) — bitte im Sendeplan nachsehen.',
  wrong_action: 'Dieser Termin muss nicht bestätigt werden.',
  confirm_failed: 'Bestätigen hat nicht geklappt — bitte erneut versuchen.',
  cancel_failed: 'Absagen hat nicht geklappt — bitte erneut versuchen.',
  no_recorded_broadcast_running: 'Gerade läuft keine Aufzeichnung.',
};

function fehlerText(err: unknown, fallback: string): string {
  const e = err as {
    response?: { data?: { error?: string; details?: unknown } };
    message?: string;
  };
  const data = e?.response?.data;
  if (typeof data?.details === 'string' && data.details) return data.details;
  if (data?.error) return FEHLER_TEXTE[data.error] || data.error;
  return e?.message || fallback;
}

const POLL_MS = 5000;
// So lange muss OBS nach „Sendung beenden" weiterlaufen (Schnittmarke): siehe obsBisMs.
const OBS_NACHLAUF_SEK = 20;
// Nachlauf-Modus (ohne Schnittmarke): so viele Sekunden nach dem OBS-Stopp startet das Werbevideo
// (Plan 2026-10-06 § A, LIVE_END_GRACE).
const NACHLAUF_SEK = 25;

// Reiter-Reihenfolge. Der Zustand lebt nur im Komponenten-State (keine ?tab=-Deep-Links).
const REITER = ['Heute', BEGRIFF_LIVE.schedule, 'Videos', 'Aufzeichnungen', 'Statistik'] as const;
const STATS_TAB = 4;

const BERLIN_UHRZEIT = new Intl.DateTimeFormat('de-DE', {
  timeZone: 'Europe/Berlin',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

/**
 * Frist für einen unbestätigten Termin, gegen die Regel in LiveShowConfirmationService.runCutoffCron:
 * um 08:50 am SENDETAG wird jeder noch unbestätigte Zusatztermin dieses Tages abgesagt. Ein Termin, der erst
 * nach 08:50 für heute angelegt wurde, wird nicht mehr abgesagt — bleibt aber unsichtbar, bis er bestätigt ist.
 */
function fristText(t: { is_today: boolean }): string {
  if (!t.is_today) {
    return `Bis morgen ${BESTAETIGEN_BIS} Uhr bestätigen — sonst wird die Sendung automatisch abgesagt.`;
  }
  const jetzt = BERLIN_UHRZEIT.format(new Date());
  if (jetzt < BESTAETIGEN_BIS) {
    return `Bis heute ${BESTAETIGEN_BIS} Uhr bestätigen — sonst wird die Sendung automatisch abgesagt.`;
  }
  return 'Bis zum Bestätigen wird der Termin nicht angekündigt (kein Countdown auf live.finanztrends.de, keine Erinnerungsmail).';
}

// R2 (2): Wirkung von „Live gehen“ bei unbestätigter Sendung heute. Belegt im Code: goLiveCore
// (routes/live.ts) setzt is_public=1 ohne Blick auf den Bestätigungsstatus und bestätigt auch nichts;
// angekündigt (Player-Termin, Newsletter, Erinnerungsmail) wird nur ein bestätigter Termin
// (LiveScheduleService.getNextShow onlyConfirmed / getTodayUpcomingShow).
const LIVE_TROTZ_UNBESTAETIGT =
  '„Live gehen“ zeigt die Sendung trotzdem sofort allen Zuschauern. Bestätigt wird der Termin dadurch nicht — angekündigt (Newsletter, Erinnerungsmail) wird nur ein bestätigter Termin.';

/** „Themen fehlen“: was das für die Ankündigung heißt (Newsletter-Karte und Player-Vorlauf lesen dieselben Themen). */
const THEMEN_FEHLEN =
  'Für diesen Tag sind noch keine Themen eingetragen — der Player auf live.finanztrends.de zeigt dann statt der Themen nur „Bleib dran“.';

// Newsletter-Versand am Sendetag (siehe Bestätigen-Dialog). Danach kündigt er heute nichts mehr an.
const NEWSLETTER_UHRZEIT = '08:54';

// R3 (Hauptbefund): Themen sind Pflicht — ohne Thema bietet „Heute“ kein Bestätigen, nur den Termin-Dialog.
// Direkt bestätigen nur bei NACHGEWIESENEN Themen: eine unbekannte Zahl (Status-Antwort fehlt, oder der
// Termin liegt > 90 min zurück und /admin/status meldet ihn nicht mehr) zählt nicht als „hat Themen".
const themenBelegt = (n?: number): boolean => typeof n === 'number' && n > 0;
const THEMEN_UNBEKANNT =
  'Bestätigen bitte über „Termin öffnen“ — die Themen ließen sich hier nicht prüfen.';
const OHNE_THEMA_KEIN_BESTAETIGEN =
  'Ohne Thema lässt sich die Sendung nicht bestätigen — Termin öffnen und mindestens ein Thema eintragen.';

// R3 (9): 08:30-Mail nach einer Absage — Wortlaut wortgleich mit Schedule.tsx absageMailSatz. Belegt in
// LiveShowConfirmationService: runReminderCron liest nur status='pending' (keine Mail für Bestätigte und
// Abgesagte); confirm() macht aus 'cancelled' wieder 'confirmed', und der Mail-Link „Bestätigen“ ruft genau
// das (handleTokenAction), solange der Sendetag nicht vorbei ist.
const REMINDER_MAIL_UHRZEIT = '08:30';
function absageMailSatz(ziel: { is_today: boolean; status: 'pending' | 'confirmed' }): string {
  const nachMail = ziel.is_today && BERLIN_UHRZEIT.format(new Date()) >= REMINDER_MAIL_UHRZEIT;
  if (!nachMail) {
    return 'Die Moderation bekommt um 08:30 Uhr keine Bestätigungs-Mail — die geht nur an noch nicht bestätigte Sendungen.';
  }
  return ziel.status === 'pending'
    ? 'Die Bestätigungs-Mail an die Moderation ist heute um 08:30 Uhr schon verschickt worden — bitte der Moderation Bescheid geben: ihr Link ‚Bestätigen‘ würde die Sendung wieder ansetzen.'
    : 'War die Sendung um 08:30 noch nicht bestätigt, ist die Bestätigungs-Mail an die Moderation schon verschickt worden — bitte der Moderation Bescheid geben: ihr Link ‚Bestätigen‘ würde die Sendung wieder ansetzen.';
}

// R3 (5): Automatik-Start. Belegt in LiveAutoGoLiveService: Tick jede Minute, Fenster Beginn bis +5 min,
// Kandidaten = alle heutigen Termine außer abgesagten und Aufzeichnungen (Bestätigung wird NICHT geprüft,
// Tests zählen mit), nur wenn OBS sendet, und je Termin nur einmal (last_auto_go_live_for_iso).
// Persona-Runde 3 (Felix, Carsten): drei Absätze unter dem Knopf waren für einen Blick vor der Kamera zu
// viel — die Kernaussage steht deshalb fett in EINER Zeile, die Einzelheiten klein darunter.
const AUTOMATIK_KURZ =
  'Sendet OBS zur Startzeit, geht die Sendung von selbst live. Sonst hier tippen — das macht sie sofort für alle sichtbar.';
const AUTOMATIK_START =
  'Von selbst heißt: bis 5 Minuten nach Beginn, auch bei einem Test oder einer noch nicht bestätigten Sendung, nie bei einer abgesagten. Tippen brauchst du, wenn OBS später startet, ohne Termin gesendet wird oder du nach „Sendung beenden“ noch einmal live willst. OBS muss dafür schon senden.';

interface McList {
  id: string;
  name: string;
}

const LiveAdmin: FC = () => {
  const istAdmin = useIstAdmin();
  const [tab, setTab] = useState(0);
  // R2 (1): „Termin öffnen“ springt in den Termin-Dialog dieses Tages (Schnittstelle zu Schedule.tsx).
  const [oeffneDatum, setOeffneDatum] = useState<string | null>(null);
  const oeffneTermin = (datum: string) => {
    setBestaetigen(null);
    setOeffneDatum(datum);
    setTab(1);
  };
  // R2 (6): „Sendung beenden“ nennt eine gerade laufende Einblendung.
  const [laufendeEinblendung, setLaufendeEinblendung] = useState<LaufendeEinblendung | null>(null);

  const [status, setStatus] = useState<LiveStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<{ msg: string; severity: 'success' | 'error' } | null>(null);
  // D9: Zeitpunkt der letzten erfolgreichen Status-Antwort. Bleibt sie aus, zeigt die Seite sonst
  // still den letzten Stand weiter — mitten in einer Sendung ist das ein falsches „läuft".
  // Startwert = Mount: antwortet der Server schon beim Oeffnen nicht, greift der Hinweis trotzdem.
  const letzteAntwortMs = useRef<number>(Date.now());
  const [veraltet, setVeraltet] = useState(false);
  const [preflight, setPreflight] = useState<Preflight | null>(null);
  const [preflightFehler, setPreflightFehler] = useState(false);
  const preflightOkMs = useRef<number | null>(null);
  const [preflightVeraltet, setPreflightVeraltet] = useState(false);

  // „Jetzt bestätigen“ (A4/A5): Rückfrage mit Wirkung, dann POST /admin/schedule/confirm.
  const [bestaetigen, setBestaetigen] = useState<BestaetigenZiel | null>(null);
  const [bestaetigtLaeuft, setBestaetigtLaeuft] = useState(false);
  const [bestaetigenFehler, setBestaetigenFehler] = useState<string | null>(null);

  // R3 (2): „Absagen …“ auf „Heute“ (orange Zeile, Sendungen heute).
  const [absagen, setAbsagen] = useState<AbsagenZiel | null>(null);
  const [absagenLaeuft, setAbsagenLaeuft] = useState(false);
  const [absagenFehler, setAbsagenFehler] = useState<string | null>(null);

  // Notfall (D5): Ausspielung bzw. Werbevideo abbrechen, je über GefahrBestaetigung.
  const [notfall, setNotfall] = useState<'aufzeichnung' | 'werbevideo' | null>(null);
  const [notfallLaeuft, setNotfallLaeuft] = useState(false);
  const [notfallFehler, setNotfallFehler] = useState<string | null>(null);

  // Mailchimp Reminder-Liste — nur für den Admin (D7). Eigener State, weil der Listen-Endpunkt langsam ist
  // (~1-2 s Mailchimp) und den Status-Poll nicht aufhalten soll.
  const [mcLists, setMcLists] = useState<McList[] | null>(null);
  const [mcListsLoading, setMcListsLoading] = useState(false);
  const [subscribeListId, setSubscribeListId] = useState<string | ''>('');
  const [subscribeSaving, setSubscribeSaving] = useState(false);

  // Terminiertes Sendungsende (Nachlauf bis zum OBS-Stopp, Plan 2026-10-06 § A). Der Countdown
  // rechnet aus der SERVER-Restzeit plus der seit dem Abruf vergangenen Browser-Zeit — nie gegen die
  // Browser-Uhr selbst (DB-Zeit ist naive Berliner Wallclock).
  const [endeTermin, setEndeTermin] = useState<{ restSek: number; abgerufenMs: number } | null>(
    null,
  );
  const [nachlaufAn, setNachlaufAn] = useState(false);
  // Schnittmarke: so viele Sekunden lässt sich „Sendung beenden“ zurücknehmen (end-stream/status cut_window_sec).
  const [schnittFensterSek, setSchnittFensterSek] = useState<number | null>(null);
  // Sendungsende per Schnittmarke (Plan 2026-10-12): jeder Player schneidet selbst am Klick, OBS ist danach egal.
  const [schnittAn, setSchnittAn] = useState(false);
  // Nach dem Klick muss OBS noch weiterlaufen: Mux veröffentlicht ein Segment erst ~5 s (gemessen bis 7,3 s)
  // nach dessen Ende, Segmente sind 4 s lang, die Marke liegt 3 s nach dem Klick (LIVE_END_CUT_OFFSET_MS) — das
  // Stück bis zur Marke steht also erst ~12–15 s nach dem Klick im Manifest. Stoppt OBS früher, fehlen den
  // Zuschauern die letzten Sekunden; nach einem OBS-Stopp hält Mux den Rest 63 s zurück (Sendung 13.10.2026).
  const [obsBisMs, setObsBisMs] = useState<number | null>(null);
  const [, setTick] = useState(0);

  const pollEnde = useCallback(async () => {
    try {
      const res = await api.get<{
        success: boolean;
        scheduled: boolean;
        grace_enabled?: boolean;
        cut_enabled?: boolean;
        cut_window_sec?: number;
        termin: { due_in_sec: number } | null;
      }>('/api/live/admin/end-stream/status');
      setNachlaufAn(res.data.grace_enabled === true);
      setSchnittAn(res.data.cut_enabled === true);
      setSchnittFensterSek(
        typeof res.data.cut_window_sec === 'number' ? res.data.cut_window_sec : null,
      );
      setEndeTermin(
        res.data.scheduled && res.data.termin
          ? { restSek: res.data.termin.due_in_sec, abgerufenMs: Date.now() }
          : null,
      );
    } catch {
      /* Admin-Route nicht erreichbar — Anzeige bleibt, wie sie war */
    }
  }, []);

  const pollStatus = useCallback(async () => {
    try {
      // Admin-Route statt /status: die oeffentliche blendet eine Test-Sendung aus, und dann war
      // „Sendung beenden" waehrend des Tests gesperrt (Vorfall 16.10.2026).
      const res = await api.get<LiveStatus>('/api/live/admin/status');
      setStatus(res.data);
      letzteAntwortMs.current = Date.now();
      setVeraltet(false);
    } catch {
      /* network blip — keep last state */
    }
    pollEnde();
  }, [pollEnde]);

  // Sekundentakt nur, solange ein Ende ansteht.
  useEffect(() => {
    if (!endeTermin) return;
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [endeTermin]);

  // OBS-Nachlauf: Sekundentakt bis zum Ablauf, der Hinweis „kann gestoppt werden" bleibt danach 60 s stehen.
  useEffect(() => {
    if (obsBisMs == null) return;
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    const weg = setTimeout(() => setObsBisMs(null), Math.max(0, obsBisMs - Date.now()) + 60_000);
    return () => {
      clearInterval(id);
      clearTimeout(weg);
    };
  }, [obsBisMs]);
  const endeRestSek = endeTermin
    ? Math.max(0, Math.round(endeTermin.restSek - (Date.now() - endeTermin.abgerufenMs) / 1000))
    : null;
  const obsRestSek = obsBisMs == null ? 0 : Math.max(0, Math.ceil((obsBisMs - Date.now()) / 1000));

  useEffect(() => {
    pollStatus();
    const id = setInterval(pollStatus, POLL_MS);
    return () => clearInterval(id);
  }, [pollStatus]);

  // Veraltet-Waechter: unabhaengig vom Poll selbst, denn ein haengender Request meldet keinen Fehler.
  useEffect(() => {
    const id = setInterval(() => {
      const zuletzt = letzteAntwortMs.current;
      setVeraltet(Date.now() - zuletzt > VERALTET_NACH_POLLS * POLL_MS);
      const pfOk = preflightOkMs.current;
      setPreflightVeraltet(pfOk != null && Date.now() - pfOk > PREFLIGHT_VERALTET_MS);
    }, 1000);
    return () => clearInterval(id);
  }, []);

  const ladePreflight = useCallback(async (): Promise<Preflight | null> => {
    try {
      const res = await api.get<{ success: boolean; data: Preflight }>(
        '/api/live/admin/preflight',
        {
          timeout: PREFLIGHT_TIMEOUT_MS,
        },
      );
      setPreflight(res.data.data);
      setPreflightFehler(false);
      preflightOkMs.current = Date.now();
      setPreflightVeraltet(false);
      return res.data.data;
    } catch {
      setPreflightFehler(true);
      return null;
    }
  }, []);

  useEffect(() => {
    if (tab !== 0) return;
    ladePreflight();
    const id = setInterval(ladePreflight, PREFLIGHT_POLL_MS);
    return () => clearInterval(id);
  }, [tab, ladePreflight]);

  // Mailchimp-Listen + aktuelle subscribe_list_id nur für den Admin laden (der Bereich ist sonst unsichtbar).
  // Beide parallel; die Listen sind der langsame Teil. Abbruch-Flag gegen ein spätes Ergebnis nach Unmount.
  useEffect(() => {
    if (!istAdmin) return;
    let cancelled = false;
    setMcListsLoading(true);
    Promise.all([
      api.get<{ success: boolean; data: McList[] }>('/api/live/admin/mailchimp-lists'),
      api.get<{ success: boolean; data: { subscribe_list_id: string | null } }>(
        '/api/live/admin/subscribe-settings',
      ),
    ])
      .then(([listsRes, settingsRes]) => {
        if (cancelled) return;
        setMcLists(listsRes.data?.data || []);
        setSubscribeListId(settingsRes.data?.data?.subscribe_list_id || '');
      })
      .catch(() => {
        if (cancelled) return;
        setMcLists([]);
        setToast({ msg: 'Mailchimp-Listen konnten nicht geladen werden', severity: 'error' });
      })
      .finally(() => {
        if (!cancelled) setMcListsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [istAdmin]);

  const handleSubscribeListSave = async () => {
    setSubscribeSaving(true);
    try {
      await api.patch('/api/live/admin/subscribe-settings', {
        subscribe_list_id: subscribeListId || null,
      });
      setToast({
        msg: subscribeListId
          ? 'Reminder-Liste gespeichert — neue Anmeldungen landen ab jetzt dort'
          : 'Sync aus — Anmeldungen landen nur noch lokal in der Datenbank',
        severity: 'success',
      });
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } }; message?: string };
      const msg = e?.response?.data?.error || e?.message || 'Speichern fehlgeschlagen';
      setToast({ msg, severity: 'error' });
    } finally {
      setSubscribeSaving(false);
    }
  };

  const isLive = !!status?.is_public;
  const isAutoRoll = status?.state === 'auto-roll';
  // Aufgezeichnete Sendung: das Backend meldet sie als `live` und trennt über
  // `broadcast_kind`. Solange sie läuft, ist „Sendung beenden" der falsche
  // Knopf — sie endet von selbst, und ein Abbruch darf keine Werbung starten.
  const isRecordedBroadcast = status?.broadcast_kind === 'recorded' && isLive;
  const isTestMode = !!status?.is_test_mode;
  const isTestSession = isLive && status?.admin_diag?.current_session?.show_action === 'test';
  const zustandHaengt = !!status?.admin_diag?.inkonsistent;
  const studioSendet = status?.admin_diag ? status.admin_diag.provider_pushing : null;
  const unbestaetigt = status?.admin_diag?.unbestaetigt ?? [];
  // Unbestätigte Sendung HEUTE: „Live gehen“ sagt dazu, was es bewirkt (R2 Punkt 2).
  const unbestaetigtHeute = unbestaetigt.find((u) => u.is_today) ?? null;

  // D6 (P3): die Rueckfrage nennt, was „Live gehen“ in der aktuellen Lage anrichtet. Der Preflight wird
  // dafuer frisch geholt — der 30-s-Stand der Karte kann eine gerade faellige Aufzeichnung verpassen.
  const handleGoLive = async () => {
    setBusy(true);
    let frisch: Preflight | null = null;
    try {
      frisch = await ladePreflight();
    } finally {
      setBusy(false);
    }
    const aufzeichnungLaeuft = isRecordedBroadcast;
    const hinweise: string[] = [];
    if (!frisch) {
      hinweise.push(
        'Die Termin-Prüfung ist gerade nicht erreichbar — ob eine Aufzeichnung ansteht, ist ungeprüft.',
      );
    }
    if (aufzeichnungLaeuft) {
      hinweise.push(
        'Gerade läuft eine Aufzeichnung. Sie endet sofort, ohne Werbevideo, und die Zuschauer sehen das Bild aus dem Studio.',
      );
    }
    if (status?.admin_diag && !status.admin_diag.provider_pushing) {
      hinweise.push(
        'Aus dem Studio kommt gerade kein Bild — die Zuschauer sähen nichts. Erst OBS starten.',
      );
    }
    if (unbestaetigtHeute && !aufzeichnungLaeuft) {
      hinweise.push(
        `Die Sendung heute um ${unbestaetigtHeute.time} Uhr ist noch nicht bestätigt. ${LIVE_TROTZ_UNBESTAETIGT}`,
      );
    }
    for (const s of frisch?.slots || []) {
      if (
        s.broadcast_source === 'recorded' &&
        s.status !== 'cancelled' &&
        (s.zustand === 'vor_frist' || s.zustand === 'ausgefallen') &&
        s.minuten_seit_start >= -15
      ) {
        hinweise.push(
          `Um ${s.time} Uhr ist eine Aufzeichnung geplant — solange die Live-Sendung läuft, startet sie nicht.`,
        );
      }
    }
    const frage =
      (aufzeichnungLaeuft
        ? 'Live übernehmen? Die laufende Aufzeichnung endet, und die Zuschauer sehen ab jetzt das Bild aus dem Studio.'
        : 'Live gehen? Die Sendung wird ab jetzt für alle Zuschauer sichtbar.') +
      (hinweise.length > 0 ? `\n\nAchtung:\n– ${hinweise.join('\n– ')}` : '');
    if (!window.confirm(frage)) return;
    setBusy(true);
    try {
      try {
        await api.post('/api/live/admin/go-live', { aufzeichnung_uebernehmen: aufzeichnungLaeuft });
      } catch (err) {
        // Die Aufzeichnung startete erst nach dem letzten Poll: dieselbe Frage, jetzt mit dem Serverstand.
        const data = (err as { response?: { data?: { error?: string; details?: string } } })
          ?.response?.data;
        if (data?.error !== 'aufzeichnung_laeuft') throw err;
        if (
          !window.confirm(
            `${data.details || 'Gerade läuft eine Aufzeichnung.'}\n\nTrotzdem live gehen? Die Aufzeichnung endet dann sofort, ohne Werbevideo.`,
          )
        )
          return;
        await api.post('/api/live/admin/go-live', { aufzeichnung_uebernehmen: true });
      }
      setToast({ msg: 'Du bist live — die Zuschauer sehen die Sendung.', severity: 'success' });
    } catch (err) {
      setToast({ msg: fehlerText(err, 'Live gehen hat nicht geklappt'), severity: 'error' });
    } finally {
      await pollStatus();
      ladePreflight();
      setBusy(false);
    }
  };

  // C7 (P3): verpasste Aufzeichnung von Hand nachstarten. Der Server prueft Termin, Video und
  // Sendeplatz selbst; ein unbestaetigter Termin wird mit dem Klick bestaetigt.
  const handleStartRecordedNow = async (slot: PreflightSlot) => {
    const frage =
      `Aufzeichnung von ${slot.time} Uhr jetzt starten? Sie läuft ab sofort für alle Zuschauer, von Anfang an.` +
      (slot.status === 'pending'
        ? '\n\nDer Termin ist noch nicht bestätigt — der Start bestätigt ihn.'
        : '');
    if (!window.confirm(frage)) return;
    setBusy(true);
    try {
      await api.post('/api/live/admin/recorded/start-now', { slot_iso: slot.iso });
      setToast({ msg: `Aufzeichnung von ${slot.time} Uhr läuft`, severity: 'success' });
    } catch (err) {
      setToast({ msg: fehlerText(err, 'Start fehlgeschlagen'), severity: 'error' });
    } finally {
      await pollStatus();
      ladePreflight();
      setBusy(false);
    }
  };

  // Werbevideo, das nach „Sendung beenden“ läuft — aus derselben Auflösung wie endStreamCore
  // (resolveAutoRollVideo, im Status als ad_video_preload). Ohne Video schaltet der Player aufs Wartebild.
  const danachText = status?.ad_video_preload
    ? `danach läuft das ${BEGRIFF_LIVE.autoRollKurz} „${status.ad_video_preload.title || 'ohne Titel'}“`
    : `danach zeigt der Player das ${BEGRIFF_LIVE.placeholder} (für heute ist kein ${BEGRIFF_LIVE.autoRollKurz} eingestellt)`;
  // D4: Wirkung von „Sendung beenden“ in einem Satz, je nach Ende-Modus (routes/live.ts end-stream).
  const endeWirkungKern = schnittAn
    ? `Die Sendung endet für die Zuschauer an der Stelle deines Klicks, ${danachText}. OBS danach noch ${OBS_NACHLAUF_SEK} Sekunden laufen lassen, dann stoppen.`
    : nachlaufAn
      ? `Nach dem Klick OBS stoppen: die Zuschauer sehen die Verabschiedung noch zu Ende, ${NACHLAUF_SEK} Sekunden nach dem OBS-Stopp ${danachText.replace(/^danach /, '')}.`
      : `Die Sendung endet sofort, ${danachText}.`;
  // R2 (6): Countdown vorher ankündigen. Es gibt ihn nur, wenn end-stream das Ende terminiert
  // (grace_enabled: Schnittmarke mit Fenster > 0 oder Nachlauf, routes/live.ts end-stream/status).
  const endeCountdown = !nachlaufAn
    ? ''
    : schnittAn
      ? ` Danach hast du ${schnittFensterSek && schnittFensterSek > 0 ? `${schnittFensterSek} Sekunden` : 'wenige Sekunden'}, um es mit „Beenden zurücknehmen“ rückgängig zu machen.`
      : ' Bis dahin läuft hier ein Countdown — solange kannst du mit „Beenden zurücknehmen“ abbrechen.';
  // R2 (6): eine laufende Einblendung zeigt der Player nur während der Sendung (index.html einblErlaubt).
  const endeEinblendung = laufendeEinblendung
    ? ` Die laufende ${BEGRIFF_LIVE.cta} „${laufendeEinblendung.headline}“ verschwindet mit der Sendung.`
    : '';
  const endeWirkung = `${endeWirkungKern}${endeCountdown}${endeEinblendung}`;

  const handleEndStream = async () => {
    const frage = `${BEGRIFF_LIVE.endStream}?\n\n${endeWirkung}`;
    if (!window.confirm(frage)) return;
    setBusy(true);
    try {
      const res = await api.post<{ success: boolean; scheduled?: boolean; due_in_sec?: number }>(
        '/api/live/admin/end-stream',
        {},
      );
      await pollStatus();
      if (schnittAn) setObsBisMs(Date.now() + OBS_NACHLAUF_SEK * 1000);
      setToast(
        schnittAn
          ? {
              msg: `Sendung beendet — die Zuschauer sehen sie bis zu dieser Stelle. OBS noch ${OBS_NACHLAUF_SEK} Sekunden laufen lassen.`,
              severity: 'success',
            }
          : res.data.scheduled
            ? {
                msg: `Sendung endet in ${res.data.due_in_sec ?? '?'} s — früher, sobald OBS gestoppt ist`,
                severity: 'success',
              }
            : { msg: 'Sendung beendet', severity: 'success' },
      );
    } catch (err) {
      setToast({ msg: fehlerText(err, 'Unbekannter Fehler'), severity: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const handleCancelEnd = async () => {
    setBusy(true);
    try {
      await api.post('/api/live/admin/end-stream/cancel', {});
      setObsBisMs(null);
      setToast({ msg: 'Beenden zurückgenommen — die Sendung läuft weiter', severity: 'success' });
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } }; message?: string };
      const code = e?.response?.data?.error;
      setToast({
        msg:
          code === 'already_due'
            ? 'Zu spät — das Ende läuft bereits'
            : code === 'nothing_scheduled'
              ? 'Es steht kein Ende an'
              : e?.message || 'Unbekannter Fehler',
        severity: 'error',
      });
    } finally {
      await pollStatus();
      setBusy(false);
    }
  };

  // Notfall (D5). Eigene Endpunkte, nicht „Sendung beenden": der startet die Werbung — nach einem
  // Abbruch ist das genau das Falsche, abgebrochen wird ja, weil etwas nicht stimmt.
  const handleNotfall = async () => {
    if (!notfall) return;
    setNotfallLaeuft(true);
    setNotfallFehler(null);
    try {
      await api.post(
        `/api/live/admin/${notfall === 'aufzeichnung' ? 'recorded/abort' : 'auto-roll/skip'}`,
        {},
      );
      setToast({
        msg: notfall === 'aufzeichnung' ? 'Aufzeichnung gestoppt' : 'Werbevideo abgebrochen',
        severity: 'success',
      });
      setNotfall(null);
      await pollStatus();
    } catch (err) {
      setNotfallFehler(fehlerText(err, 'Abbrechen hat nicht geklappt'));
    } finally {
      setNotfallLaeuft(false);
    }
  };

  // A4/A5: Termin bestätigen (dieselbe Route wie „Bestätigen“ im Sendeplan).
  const oeffneBestaetigen = (ziel: BestaetigenZiel) => {
    setBestaetigenFehler(null);
    setBestaetigen(ziel);
  };
  const handleBestaetigen = async () => {
    // R3: ohne Thema kein Bestätigen — auch nicht über einen verirrten Aufruf (Knopf fehlt dann ohnehin).
    if (!bestaetigen || !themenBelegt(bestaetigen.themen_anzahl)) return;
    setBestaetigtLaeuft(true);
    setBestaetigenFehler(null);
    try {
      await api.post('/api/live/admin/schedule/confirm', {
        show_date: bestaetigen.date,
        action: bestaetigen.source,
      });
      setToast({
        msg: `Bestätigt — die Sendung ${bestaetigen.is_today ? 'heute' : 'morgen'} um ${bestaetigen.time} Uhr ist jetzt für Zuschauer angekündigt.`,
        severity: 'success',
      });
      setBestaetigen(null);
      await pollStatus();
      ladePreflight();
    } catch (err) {
      setBestaetigenFehler(fehlerText(err, 'Bestätigen hat nicht geklappt'));
    } finally {
      setBestaetigtLaeuft(false);
    }
  };

  // R3 (2): Absagen von „Heute“ aus — dieselbe Route und derselbe Body wie Schedule.tsx handleCancelShow.
  const oeffneAbsagen = (ziel: AbsagenZiel) => {
    setAbsagenFehler(null);
    setAbsagen(ziel);
  };
  const handleAbsagen = async () => {
    if (!absagen) return;
    setAbsagenLaeuft(true);
    setAbsagenFehler(null);
    try {
      await api.post('/api/live/admin/schedule/cancel', {
        show_date: absagen.date,
        action: absagen.source,
      });
      setToast({ msg: 'Sendung abgesagt.', severity: 'success' });
      setAbsagen(null);
      await pollStatus();
      ladePreflight();
    } catch (err) {
      setAbsagenFehler(fehlerText(err, 'Absage fehlgeschlagen'));
    } finally {
      setAbsagenLaeuft(false);
    }
  };
  const absagenTag = absagen?.is_today ? 'heute' : 'morgen';
  const absagenNewsletterRaus =
    !!absagen?.is_today && BERLIN_UHRZEIT.format(new Date()) >= NEWSLETTER_UHRZEIT;

  // Backend stores Berlin wall-clock; mysql2 returns ISO string with Z suffix
  // where the numeric value IS Berlin time. Slice the time portion only.
  const startedAtTime = status?.started_at
    ? (String(status.started_at).match(/T(\d{2}:\d{2})/)?.[1] ?? '')
    : '';

  // Statuszeile ohne Fachbegriffe (D8). Technik (Sendungs-Nr., Werbevideo-Nr.) steht im Admin-Bereich.
  const kopfzeile = !status
    ? 'Status wird geladen …'
    : isAutoRoll
      ? `${BEGRIFF_LIVE.autoRoll} läuft — die Sendung ist beendet`
      : isTestSession
        ? 'Test-Sendung läuft — nur mit Test-Link sichtbar'
        : isRecordedBroadcast
          ? 'Aufzeichnung läuft — für alle Zuschauer sichtbar'
          : isLive
            ? 'Live — die Sendung läuft'
            : `Nicht live — der Player zeigt das ${BEGRIFF_LIVE.placeholder}`;
  // R2 (3): eine unbestätigte Sendung heute/morgen, die VOR der nächsten bestätigten liegt, nicht
  // überspringen. `next_show_text` nennt nur bestätigte Termine (der Zuschauertext).
  const ersteUnbestaetigt = unbestaetigt[0] ?? null;
  const unbestaetigtZuerst =
    ersteUnbestaetigt != null &&
    (!status?.next_show || ersteUnbestaetigt.iso < String(status.next_show.iso));
  const bestaetigtText = (status?.next_show_text || '').replace(/^Nächste Sendung\s+/, '');
  const naechsteText = unbestaetigtZuerst
    ? `Nächste Sendung: ${ersteUnbestaetigt.is_today ? 'heute' : 'morgen'} ${ersteUnbestaetigt.time} Uhr${ersteUnbestaetigt.broadcast_source === 'recorded' ? ' (Aufzeichnung)' : ''} — noch nicht bestätigt.${status?.next_show ? ` Danach bestätigt: ${bestaetigtText}` : ' Keine bestätigte Sendung geplant.'}`
    : status?.next_show_text || '';
  const unterzeile = !status
    ? ''
    : isAutoRoll
      ? 'Danach geht der Player von selbst offline. Nichts zu tun.'
      : isRecordedBroadcast
        ? `Läuft seit ${startedAtTime ? `${startedAtTime} Uhr` : 'kurzem'} und endet von selbst; danach läuft das ${BEGRIFF_LIVE.autoRollKurz}.`
        : isLive
          ? startedAtTime
            ? `Live seit ${startedAtTime} Uhr.`
            : ''
          : naechsteText;

  const einblendungen = <Einblendungen onLaufAenderung={setLaufendeEinblendung} />;
  const heuteKarte = (
    <HeuteKarte
      preflight={preflight}
      fehler={preflightFehler}
      veraltet={preflightVeraltet}
      standMs={preflightOkMs.current}
      sendeplatzBelegt={isLive}
      busy={busy}
      unbestaetigt={unbestaetigt}
      onStartNow={handleStartRecordedNow}
      onBestaetigen={oeffneBestaetigen}
      onTerminOeffnen={oeffneTermin}
      onAbsagen={oeffneAbsagen}
    />
  );

  return (
    // 900px tut den formularlastigen Tabs gut, schneidet aber die
    // Statistik-Tabelle ab (17 Spalten). Nur dieser Tab bekommt volle Breite.
    <Box m="20px" maxWidth={900}>
      <Typography variant="h4" fontWeight="bold" mb={1}>
        Live Admin
      </Typography>
      <Typography variant="body2" color="textSecondary" mb={2}>
        Steuerung der täglichen Live-Sendung auf live.finanztrends.de.
      </Typography>

      <Tabs
        value={tab}
        onChange={(_, v) => {
          setOeffneDatum(null);
          setTab(v);
        }}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        {REITER.map((r) => (
          <Tab key={r} label={r} />
        ))}
      </Tabs>

      {tab === 0 && (
        <Box>
          {veraltet && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Status veraltet — keine Antwort vom Server seit{' '}
              {moment(letzteAntwortMs.current).format('HH:mm:ss')} Uhr. Die Anzeige unten zeigt den
              letzten bekannten Stand und kann falsch sein. Seite neu laden; hilft das nicht:{' '}
              {TECHNIK_KONTAKT}.
            </Alert>
          )}
          {zustandHaengt && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Der Live-Zustand hängt: das System steht auf „live“, aber es läuft keine Sendung.
              Geplante Sendungen und Aufzeichnungen würden so nicht starten. Das repariert sich in
              etwa 1 Minute selbst — sonst „{BEGRIFF_LIVE.endStream}“ klicken.
            </Alert>
          )}
          {isTestSession && !zustandHaengt && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Test-Sendung läuft (Zuschauer sehen sie nur mit Test-Link). Nach dem Test „
              {BEGRIFF_LIVE.endStream}“ klicken — sonst endet sie erst etwa 5 Minuten nach dem
              OBS-Stopp von selbst.
              {status?.admin_diag?.test_vorschau_url && (
                <>
                  {' '}
                  <MuiLink
                    href={status.admin_diag.test_vorschau_url}
                    target="_blank"
                    rel="noopener"
                    underline="hover"
                  >
                    Test-Vorschau öffnen
                  </MuiLink>
                </>
              )}
            </Alert>
          )}

          {/* ── Sendepult: Status, Signal aus dem Studio, Live gehen / Sendung beenden (D1–D4, D8) ── */}
          <Paper sx={{ p: 2.5, mb: 2 }}>
            {/* Umbrechen statt quetschen: auf dem Handy rutscht „Player ansehen" unter die Statuszeile. */}
            <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
              <FiberManualRecord
                sx={{
                  // Aufzeichnung lila wie im Kalender — ein roter Punkt las sich als „ich bin live“ (R3, Carsten).
                  color: isRecordedBroadcast ? '#7b3fbf' : isLive ? 'error.main' : 'text.disabled',
                  fontSize: 28,
                  flex: '0 0 auto',
                  ...(isLive && {
                    animation: 'pulse 1.6s ease-in-out infinite',
                    '@keyframes pulse': {
                      '0%, 100%': { opacity: 1 },
                      '50%': { opacity: 0.4 },
                    },
                  }),
                }}
              />
              {/* 160 px Basis: Punkt + Text passen am Handy in eine Zeile, nur „Player ansehen" bricht um. */}
              <Box sx={{ flex: '1 1 160px', minWidth: 0 }}>
                <Typography variant="h6" fontWeight={600} sx={{ lineHeight: 1.3 }}>
                  {kopfzeile}
                </Typography>
                {unterzeile && (
                  <Typography variant="body2" color="textSecondary">
                    {unterzeile}
                  </Typography>
                )}
              </Box>
              <MuiLink
                href="https://live.finanztrends.de/"
                target="_blank"
                rel="noopener"
                underline="hover"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  fontSize: 13,
                  flex: '0 0 auto',
                }}
              >
                Player ansehen <OpenInNewOutlined sx={{ fontSize: 14 }} />
              </MuiLink>
            </Box>

            {/* A5: eine unbestätigte Sendung heute/morgen nie still überspringen. R2 (1, 4): Hauptweg ist
                der Termin-Dialog (Themen, Schlagzeile, Bestätigen, Absagen); Knöpfe UNTER dem Text, damit
                die Zeile auf dem Handy nicht gequetscht wird. */}
            {!isLive &&
              unbestaetigt.map((u) => (
                <Alert
                  key={u.iso}
                  severity="warning"
                  sx={{
                    mt: 1.5,
                    '& .MuiAlert-message': { overflow: 'visible', flex: 1, minWidth: 0 },
                  }}
                >
                  <strong>
                    {u.is_today ? 'Heute' : 'Morgen'} {u.time} Uhr
                    {u.broadcast_source === 'recorded' ? ' (Aufzeichnung)' : ''} — noch nicht
                    bestätigt.
                  </strong>{' '}
                  {fristText(u)}
                  {u.themen_anzahl === 0 && <> {THEMEN_FEHLEN}</>}
                  <Stack
                    direction="row"
                    spacing={1}
                    mt={1}
                    flexWrap="wrap"
                    useFlexGap
                    alignItems="center"
                  >
                    <Button
                      color="warning"
                      size="small"
                      variant="contained"
                      disabled={busy}
                      onClick={() => oeffneTermin(u.date)}
                    >
                      Termin öffnen
                    </Button>
                    {/* R3 (Hauptbefund): ohne Thema kein Bestätigen — nur der Termin-Dialog. */}
                    {themenBelegt(u.themen_anzahl) && (
                      <Button
                        color="inherit"
                        size="small"
                        disabled={busy}
                        onClick={() => oeffneBestaetigen(u)}
                      >
                        Jetzt bestätigen
                      </Button>
                    )}
                    {istAbsagbareQuelle(u.source) && (
                      <Button
                        color="error"
                        size="small"
                        disabled={busy}
                        onClick={() => oeffneAbsagen({ ...u, status: 'pending' })}
                      >
                        Absagen …
                      </Button>
                    )}
                  </Stack>
                  {!themenBelegt(u.themen_anzahl) && (
                    <Typography variant="caption" component="div" sx={{ mt: 0.75 }}>
                      {u.themen_anzahl === 0 ? OHNE_THEMA_KEIN_BESTAETIGEN : THEMEN_UNBEKANNT}
                    </Typography>
                  )}
                </Alert>
              ))}

            {/* D2: Signal aus dem Studio direkt bei „Live gehen“. Bei Aufzeichnung/Werbevideo ohne Belang. */}
            {studioSendet != null && !isAutoRoll && !isRecordedBroadcast && (
              <Box
                sx={{
                  mt: 1.5,
                  px: 1.5,
                  py: 1,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: studioSendet ? '#b7e4c7' : isLive ? '#f5c2bd' : '#ffd8a8',
                  bgcolor: studioSendet ? '#e6f5ec' : isLive ? '#fdecea' : '#fff4e5',
                  color: studioSendet ? '#0A6634' : isLive ? '#a12a1f' : '#8a4b00',
                  fontSize: 14,
                }}
              >
                <strong>{BEGRIFF_LIVE.obsSignal}:</strong>{' '}
                {studioSendet
                  ? 'Bild kommt an (OBS sendet).'
                  : isLive
                    ? 'Kein Bild — die Zuschauer sehen gerade nichts. OBS prüfen.'
                    : 'Noch kein Bild — erst OBS starten, dann live gehen.'}
              </Box>
            )}

            <Stack spacing={1.25} mt={2}>
              {isRecordedBroadcast ? (
                // D3: während einer Aufzeichnung ist das Übernehmen eine Ausnahme, kein roter Hauptknopf.
                <>
                  <Button
                    variant="outlined"
                    color="warning"
                    size="large"
                    startIcon={<PlayCircleOutline />}
                    disabled={busy}
                    onClick={handleGoLive}
                  >
                    Live übernehmen (Aufzeichnung endet)
                  </Button>
                  <Typography variant="caption" color="textSecondary">
                    Nur wenn du statt der Aufzeichnung jetzt live senden willst: sie endet sofort,
                    ohne {BEGRIFF_LIVE.autoRollKurz}, die Zuschauer sehen das Bild aus dem Studio.
                  </Typography>
                </>
              ) : (
                <>
                  <Button
                    variant="contained"
                    color="error"
                    size="large"
                    startIcon={<PlayCircleOutline />}
                    disabled={busy || isLive}
                    onClick={handleGoLive}
                    sx={{ fontSize: 16, py: 1.5 }}
                  >
                    {BEGRIFF_LIVE.goLive}
                  </Button>
                  {!isLive && (
                    <Typography variant="caption" color="textSecondary">
                      <Box component="strong" sx={{ display: 'block', color: 'text.primary' }}>
                        {AUTOMATIK_KURZ}
                      </Box>
                      <Box component="span" sx={{ display: 'block', mt: 0.5 }}>
                        {AUTOMATIK_START}
                        {unbestaetigtHeute &&
                          ` Die Sendung heute um ${unbestaetigtHeute.time} Uhr ist noch nicht bestätigt: ${LIVE_TROTZ_UNBESTAETIGT}`}
                      </Box>
                    </Typography>
                  )}
                </>
              )}

              <Button
                variant="outlined"
                startIcon={<StopCircleOutlined />}
                disabled={
                  busy || !isLive || isAutoRoll || isRecordedBroadcast || endeTermin != null
                }
                onClick={handleEndStream}
              >
                {BEGRIFF_LIVE.endStream}
              </Button>
              {isLive && !isAutoRoll && !isRecordedBroadcast && endeTermin == null && (
                <Typography variant="caption" color="textSecondary">
                  {endeWirkung}
                </Typography>
              )}
              {endeTermin && (
                <Alert
                  severity="warning"
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      disabled={busy}
                      onClick={handleCancelEnd}
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      Beenden zurücknehmen
                    </Button>
                  }
                >
                  {endeRestSek != null && endeRestSek > 0
                    ? schnittAn
                      ? `Sendung beendet — noch ${endeRestSek} s zum Zurücknehmen.`
                      : `Sendung endet in ${Math.floor(endeRestSek / 60)}:${String(endeRestSek % 60).padStart(2, '0')} — früher, sobald OBS gestoppt ist (+${NACHLAUF_SEK} s).`
                    : 'Sendung wird beendet …'}
                </Alert>
              )}
              {schnittAn && obsBisMs != null && (
                <Alert severity={obsRestSek > 0 ? 'info' : 'success'}>
                  {obsRestSek > 0
                    ? `OBS noch ${obsRestSek} s laufen lassen — die Zuschauer sehen gerade noch das Ende.`
                    : 'OBS kann jetzt gestoppt werden.'}
                </Alert>
              )}
            </Stack>
          </Paper>

          {/* R3 (6): feste Reihenfolge in jedem Zustand — Sendepult, Einblendungen, Sendungen heute, Notfall,
              Wenn etwas klemmt. Der Zustandswechsel aus R2 verwirrte am zweiten Tag. */}
          {einblendungen}
          {heuteKarte}

          {/* D5: Notbremsen eingeklappt, jede mit Rückfrage und Wirkung in Klartext. */}
          <Abschnitt
            titel="Notfall"
            eingeklappt
            erklaerung="Nur wenn etwas schiefläuft: eine laufende Aufzeichnung stoppen oder das Werbevideo sofort abbrechen."
          >
            <Stack spacing={2}>
              <Box>
                <Button
                  variant="outlined"
                  color="warning"
                  startIcon={<StopCircleOutlined />}
                  disabled={busy || !isRecordedBroadcast}
                  onClick={() => {
                    setNotfallFehler(null);
                    setNotfall('aufzeichnung');
                  }}
                >
                  Aufzeichnung stoppen
                </Button>
                <Typography variant="caption" color="textSecondary" component="div" mt={0.5}>
                  Stoppt eine laufende Aufzeichnung sofort, ohne {BEGRIFF_LIVE.autoRollKurz}.
                  {!isRecordedBroadcast && ' Gerade läuft keine Aufzeichnung.'}
                </Typography>
              </Box>
              <Box>
                <Button
                  variant="outlined"
                  color="warning"
                  startIcon={<StopCircleOutlined />}
                  disabled={busy || !isAutoRoll}
                  onClick={() => {
                    setNotfallFehler(null);
                    setNotfall('werbevideo');
                  }}
                >
                  {BEGRIFF_LIVE.autoRollKurz} abbrechen
                </Button>
                <Typography variant="caption" color="textSecondary" component="div" mt={0.5}>
                  Stoppt das {BEGRIFF_LIVE.autoRoll} sofort.
                  {!isAutoRoll && ` Gerade läuft kein ${BEGRIFF_LIVE.autoRollKurz}.`}
                </Typography>
              </Box>
            </Stack>
          </Abschnitt>

          {/* D6/D7: Hilfe statt „Hinweise“. */}
          <Abschnitt
            titel="Wenn etwas klemmt"
            eingeklappt
            erklaerung="Was du selbst prüfen kannst — und wen du fragst."
          >
            <Box component="ul" sx={{ pl: 2.5, mt: 0, mb: 0, fontSize: 14, '& li': { mb: 1 } }}>
              <li>
                <strong>Einblendung erscheint nicht:</strong> Zuschauer sehen das Bild 20–30
                Sekunden später als du — kurz abwarten. Steht oben bei den Einblendungen der grüne
                Kasten „Läuft: …“? Einblenden geht nur, während eine Sendung läuft (Status oben:
                „Live“).
              </li>
              <li>
                <strong>Zuschauer sehen kein Bild:</strong> Zeile „{BEGRIFF_LIVE.obsSignal}“ im
                Sendepult prüfen. Steht dort „kein Bild“, OBS neu starten.
              </li>
              <li>
                <strong>„{BEGRIFF_LIVE.goLive}“ macht die Sendung nur sichtbar</strong> — OBS muss
                vorher schon senden.
              </li>
              <li>
                <strong>Anzeige bewegt sich nicht:</strong> Der Status aktualisiert sich alle 5
                Sekunden. Steht oben „Status veraltet“, die Seite neu laden.
              </li>
              <li>
                <strong>Test-Sendung:</strong> wird im Reiter „{BEGRIFF_LIVE.schedule}“ als Termin
                angelegt.
              </li>
              <li>
                <strong>Lädt das Dashboard nicht:</strong> Notfall-Fernbedienung
                live.finanztrends.de/admin.html — dort gehen „{BEGRIFF_LIVE.goLive}“, „
                {BEGRIFF_LIVE.endStream}“ und die Einblendungen. Den Zugangsschlüssel gibt dir{' '}
                {TECHNIK_KONTAKT} — <strong>vorher</strong> holen und auf dem Gerät speichern, im
                Notfall während der Sendung ist dafür keine Zeit.
              </li>
              <li>
                <strong>Hilft das alles nicht:</strong> {TECHNIK_KONTAKT}.
              </li>
            </Box>
          </Abschnitt>

          {/* D7/D8: Technik nur für den Admin. */}
          <NurAdmin
            bereich
            titel="Technik"
            erklaerung="Diagnose, Mailchimp-Liste, Notfall-Fernbedienung."
          >
            <Typography variant="subtitle2" gutterBottom>
              Diagnose
            </Typography>
            <Box
              component="ul"
              sx={{ pl: 2.5, mt: 0, mb: 2, fontSize: 13, color: 'text.secondary' }}
            >
              <li>
                Zustand: {status?.state ?? '?'} · is_public {String(status?.is_public ?? '?')} ·
                Session {status?.session_id ?? '–'}
                {status?.admin_diag?.current_session?.show_action
                  ? ` (${status.admin_diag.current_session.show_action})`
                  : ''}
              </li>
              <li>
                Auto-Roll-Video #{status?.ad_video_id ?? '–'} · Preload #
                {status?.ad_video_preload?.id ?? '–'}
              </li>
              <li>
                Mux-Push: {studioSendet == null ? '?' : studioSendet ? 'aktiv' : 'idle'} · aktiv
                seit {status?.admin_diag?.provider_active_since ?? '–'} · idle seit{' '}
                {status?.admin_diag?.provider_idle_at ?? '–'}
              </li>
              <li>
                Test-Gate: {status?.admin_diag?.test_gate_aktiv ? 'aktiv' : 'aus'}
                {isTestMode ? ' (Test-Modus)' : ''} · Test-Link:{' '}
                {status?.admin_diag?.test_vorschau_url ? (
                  <MuiLink
                    href={status.admin_diag.test_vorschau_url}
                    target="_blank"
                    rel="noopener"
                  >
                    öffnen
                  </MuiLink>
                ) : (
                  'nicht eingerichtet (FT_LIVE_TEST_TOKEN fehlt)'
                )}
              </li>
              <li>
                Ende-Modus: {schnittAn ? 'Schnittmarke' : nachlaufAn ? 'Nachlauf' : 'sofort'} ·
                Unbestätigt-Liste:{' '}
                {status?.admin_diag?.unbestaetigt == null ? 'nicht lesbar' : unbestaetigt.length}
              </li>
              <li>
                Simulcast: Mux leitet jeden Push auch an YouTube weiter (seit 30.09.2026) — auch
                Test-Sendungen.
              </li>
            </Box>

            <Typography variant="subtitle2" gutterBottom>
              Mailchimp Reminder-Liste
            </Typography>
            <Typography variant="body2" color="textSecondary" mb={1.5}>
              Ziel-Liste für Anmeldungen aus dem Formular auf live.finanztrends.de (Finanztrends
              MC-Account, Tag „Live-Reminder“). Reminder-Mails versendet Marketing aus Mailchimp.
            </Typography>
            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
              <TextField
                select
                label="Reminder-Liste"
                value={subscribeListId}
                onChange={(e) => setSubscribeListId(e.target.value)}
                disabled={mcListsLoading || subscribeSaving}
                sx={{ minWidth: { xs: 0, sm: 360 }, flex: '1 1 260px' }}
                InputProps={{
                  endAdornment: mcListsLoading ? (
                    <CircularProgress size={16} sx={{ mr: 1 }} />
                  ) : undefined,
                }}
              >
                <MenuItem value="">
                  <em>— Sync deaktivieren —</em>
                </MenuItem>
                {(mcLists || []).map((list) => (
                  <MenuItem key={list.id} value={list.id}>
                    {list.name} ({list.id})
                  </MenuItem>
                ))}
                {/* Fallback: die gespeicherte Liste fehlt in der MC-Antwort (geloescht, umbenannt,
                    anderes Konto). Ohne diesen Eintrag wirft MUI „out-of-range value" und das Feld
                    stuende leer da, als waere der Sync aus. */}
                {subscribeListId && mcLists && !mcLists.some((l) => l.id === subscribeListId) && (
                  <MenuItem value={subscribeListId}>
                    <em>(unbekannte Liste — {subscribeListId} nicht im MC-Account gefunden)</em>
                  </MenuItem>
                )}
              </TextField>
              <Button
                variant="contained"
                onClick={handleSubscribeListSave}
                disabled={subscribeSaving || mcListsLoading}
              >
                {subscribeSaving ? 'Speichern…' : 'Speichern'}
              </Button>
            </Stack>
            <Typography
              variant="caption"
              color={subscribeListId ? 'success.main' : 'warning.main'}
              sx={{ display: 'block', mt: 1.5, mb: 2 }}
            >
              {subscribeListId
                ? `Sync aktiv — neue Anmeldungen landen in Liste ${subscribeListId}`
                : 'Sync aus — Anmeldungen landen nur lokal (live_show_subscribers)'}
            </Typography>

            <Typography variant="subtitle2" gutterBottom>
              Notfall-Fernbedienung ohne Dashboard-Login
            </Typography>
            <Typography variant="caption" color="textSecondary">
              <code style={{ fontSize: 11 }}>https://live.finanztrends.de/admin.html</code> —
              Zugangsschlüssel = <code style={{ fontSize: 11 }}>LIVE_ADMIN_TOKEN</code> aus der
              Server-Umgebung. Den Schlüssel nur direkt weitergeben, nie als Link.
            </Typography>
          </NurAdmin>
        </Box>
      )}

      {tab === 1 && (
        <Schedule oeffneDatum={oeffneDatum} onOeffneDatumErledigt={() => setOeffneDatum(null)} />
      )}

      {tab === 2 && <AdVideos />}

      {tab === 3 && <Replays />}

      {tab === STATS_TAB && <Stats />}

      <GefahrBestaetigung
        open={notfall != null}
        titel={
          notfall === 'aufzeichnung'
            ? 'Aufzeichnung stoppen?'
            : `${BEGRIFF_LIVE.autoRollKurz} abbrechen?`
        }
        wirkung={
          notfall === 'aufzeichnung'
            ? `Die Aufzeichnung stoppt sofort für alle Zuschauer. Der Player geht offline und zeigt das ${BEGRIFF_LIVE.placeholder} — es läuft KEIN ${BEGRIFF_LIVE.autoRollKurz}. Das lässt sich nicht rückgängig machen.`
            : `Das ${BEGRIFF_LIVE.autoRollKurz} stoppt sofort. Der Player geht offline, die eingebundenen Player auf finanztrends.de und den Partnerseiten verschwinden.`
        }
        knopfText={
          notfall === 'aufzeichnung'
            ? 'Aufzeichnung stoppen'
            : `${BEGRIFF_LIVE.autoRollKurz} abbrechen`
        }
        laeuft={notfallLaeuft}
        fehler={notfallFehler}
        onBestaetigen={handleNotfall}
        onAbbrechen={() => setNotfall(null)}
      />

      <Dialog
        open={bestaetigen != null}
        onClose={bestaetigtLaeuft ? undefined : () => setBestaetigen(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Sendung {bestaetigen?.is_today ? 'heute' : 'morgen'} um {bestaetigen?.time} Uhr
          bestätigen?
        </DialogTitle>
        <DialogContent>
          {/* R3 (Hauptbefund): Themen sind Pflicht — ohne Thema bietet die Rückfrage kein Bestätigen,
              nur „Termin öffnen“ (das Backend sperrt nicht, deshalb hier). */}
          {bestaetigen?.themen_anzahl === 0 && (
            <Alert severity="warning" sx={{ mb: 1.5 }}>
              <strong>Themen fehlen.</strong> {OHNE_THEMA_KEIN_BESTAETIGEN} {THEMEN_FEHLEN}
            </Alert>
          )}
          {themenBelegt(bestaetigen?.themen_anzahl) && (
            <>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {bestaetigen?.is_today && BERLIN_UHRZEIT.format(new Date()) >= NEWSLETTER_UHRZEIT
                  ? 'Danach findet die Sendung sicher statt: Zuschauer sehen den Termin auf live.finanztrends.de, und 15 Minuten vor Beginn geht die Erinnerungsmail raus (sofern noch Zeit ist). Der Newsletter ist heute schon verschickt.'
                  : `Danach findet die Sendung sicher statt: Zuschauer sehen den Termin auf live.finanztrends.de, der Newsletter am Sendetag (${NEWSLETTER_UHRZEIT} Uhr) kündigt sie an, und 15 Minuten vor Beginn geht die Erinnerungsmail raus.`}
                {bestaetigen?.broadcast_source === 'recorded' &&
                  ' Die Aufzeichnung startet zur geplanten Zeit von selbst.'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Fällt die Sendung doch aus: „Absagen …“ hier auf „Heute“ oder im Reiter „
                {BEGRIFF_LIVE.schedule}“.
              </Typography>
            </>
          )}
          {bestaetigenFehler && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {bestaetigenFehler}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Button onClick={() => setBestaetigen(null)} disabled={bestaetigtLaeuft}>
            Abbrechen
          </Button>
          {bestaetigen && (
            <Button
              variant={themenBelegt(bestaetigen.themen_anzahl) ? 'outlined' : 'contained'}
              color="warning"
              disabled={bestaetigtLaeuft}
              onClick={() => oeffneTermin(bestaetigen.date)}
            >
              Termin öffnen
            </Button>
          )}
          {themenBelegt(bestaetigen?.themen_anzahl) && (
            <Button
              variant="contained"
              color="success"
              onClick={handleBestaetigen}
              disabled={bestaetigtLaeuft}
              startIcon={
                bestaetigtLaeuft ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <CheckCircleOutline />
                )
              }
            >
              Jetzt bestätigen
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* R3 (2): Absagen von „Heute“ — Wirkung wie im Termin-Dialog (Schedule.tsx absagenOffen), plus 08:30-Mail. */}
      <GefahrBestaetigung
        open={absagen != null}
        titel={`Sendung ${absagenTag} um ${absagen?.time ?? ''} Uhr absagen?`}
        wirkung={
          <>
            Die Sendung verschwindet für Zuschauer von live.finanztrends.de (dort steht dann die
            nächste bestätigte Sendung). Der Newsletter des Sendetags weist nicht darauf hin, es
            geht keine Erinnerungsmail raus, und sie geht auch bei Signal aus dem Studio nicht von
            selbst live
            {absagen?.broadcast_source === 'recorded' ? ' — die Aufzeichnung startet nicht' : ''}.
            {absagenNewsletterRaus &&
              ` Der Newsletter von heute ist schon verschickt (${NEWSLETTER_UHRZEIT} Uhr) — hat er die Sendung angekündigt, lässt sich das nicht mehr zurückholen.`}{' '}
            {absagen ? absageMailSatz(absagen) : ''} Der Termin bleibt im {BEGRIFF_LIVE.schedule}{' '}
            (rot) und lässt sich dort wieder ansetzen.
          </>
        }
        knopfText="Sendung absagen"
        laeuft={absagenLaeuft}
        fehler={absagenFehler}
        onBestaetigen={handleAbsagen}
        onAbbrechen={() => setAbsagen(null)}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={toast?.severity === 'error' ? 8000 : 4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert
            severity={toast.severity}
            variant="filled"
            onClose={() => setToast(null)}
            sx={{ minWidth: 280 }}
          >
            {toast.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

// D5 (P3): „Heute" — wird jeder Termin starten, und wenn nicht, warum. Regeln und Texte kommen
// vom Server (dieselben Module wie Start und Alarm), hier wird nur dargestellt.
const HeuteKarte: FC<{
  preflight: Preflight | null;
  fehler: boolean;
  veraltet: boolean;
  standMs: number | null;
  sendeplatzBelegt: boolean;
  busy: boolean;
  unbestaetigt: UnbestaetigterTermin[];
  onStartNow: (slot: PreflightSlot) => void;
  onBestaetigen: (ziel: BestaetigenZiel) => void;
  onTerminOeffnen: (datum: string) => void;
  onAbsagen: (ziel: AbsagenZiel) => void;
}> = ({
  preflight,
  fehler,
  veraltet,
  standMs,
  sendeplatzBelegt,
  busy,
  unbestaetigt,
  onStartNow,
  onBestaetigen,
  onTerminOeffnen,
  onAbsagen,
}) => (
  <Paper sx={{ p: 2.5, mb: 2 }}>
    <Typography variant="overline" color="textSecondary" fontWeight={600}>
      Sendungen heute
    </Typography>
    {fehler && !preflight && (
      <Typography variant="body2" color="error.main" mt={1}>
        Die Termin-Prüfung konnte nicht geladen werden.
      </Typography>
    )}
    {preflight && (fehler || veraltet) && (
      <Alert severity="warning" sx={{ mt: 1 }}>
        Termin-Prüfung nicht aktuell — Stand{' '}
        {standMs ? `${moment(standMs).format('HH:mm:ss')} Uhr` : 'unbekannt'}, die Aktualisierung
        schlägt fehl.
      </Alert>
    )}
    {!fehler && !preflight && <CircularProgress size={16} sx={{ display: 'block', mt: 1 }} />}
    {preflight && preflight.slots.length === 0 && (
      <Typography variant="body2" color="textSecondary" mt={1}>
        Heute ist keine Sendung geplant.
      </Typography>
    )}
    {preflight?.slots.map((slot) => {
      const zustand = ZUSTAND_TEXT[slot.zustand] || {
        label: slot.zustand,
        color: 'default' as const,
      };
      const nachstartbar =
        slot.broadcast_source === 'recorded' &&
        (slot.zustand === 'ausgefallen' || slot.zustand === 'nach_fenster') &&
        !sendeplatzBelegt;
      // A4: unbestätigt → direkt bestätigen. Test-Termine brauchen keine Bestätigung (immer „bestätigt“).
      const bestaetigbar =
        slot.status === 'pending' && slot.zustand !== 'test' && slot.zustand !== 'abgesagt';
      // R3 (2): absagen, solange der Termin noch bevorsteht oder gerade nicht gestartet ist. Eine laufende
      // Sendung lehnt der Server ab (termin_laeuft) — die Meldung steht dann in der Rückfrage.
      const absagbar =
        istAbsagbareQuelle(slot.source) &&
        slot.status !== 'cancelled' &&
        (slot.zustand === 'vor_frist' || slot.zustand === 'ausgefallen');
      // Themenzahl aus der Status-Antwort (derselbe Termin); fehlt sie, keine Warnung.
      const themenAnzahl = unbestaetigt.find((u) => u.iso === slot.iso)?.themen_anzahl;
      return (
        <Box key={slot.iso} mt={1.5}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="subtitle1" fontWeight={600}>
              {slot.time} Uhr
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {slot.zustand === 'test'
                ? 'Test-Sendung'
                : slot.broadcast_source === 'recorded'
                  ? 'Aufzeichnung'
                  : 'Live-Sendung'}
            </Typography>
            <Chip size="small" label={zustand.label} color={zustand.color} />
            {slot.status === 'confirmed' && slot.zustand !== 'test' && (
              <Chip size="small" label="bestätigt" color="success" variant="outlined" />
            )}
            {nachstartbar && (
              <Button
                size="small"
                variant="contained"
                color="warning"
                startIcon={<PlayCircleOutline />}
                disabled={busy}
                onClick={() => onStartNow(slot)}
              >
                Aufzeichnung jetzt starten
              </Button>
            )}
          </Stack>
          <Box component="ul" sx={{ pl: 2.5, mt: 0.5, mb: 0, fontSize: 13 }}>
            {slot.punkte.map((p, i) => (
              <Box component="li" key={i} sx={{ color: STUFE_FARBE[p.stufe] }}>
                {p.text}
              </Box>
            ))}
          </Box>
          {(bestaetigbar || absagbar) && (
            <Box
              sx={{
                mt: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                flexWrap: 'wrap',
              }}
            >
              {bestaetigbar && (
                <Button
                  size="small"
                  variant="contained"
                  color="warning"
                  disabled={busy}
                  onClick={() => onTerminOeffnen(preflight.heute)}
                >
                  Termin öffnen
                </Button>
              )}
              {/* R3 (Hauptbefund): ohne Thema kein Bestätigen — nur der Termin-Dialog. */}
              {bestaetigbar && themenBelegt(themenAnzahl) && (
                <Button
                  size="small"
                  color="success"
                  startIcon={<CheckCircleOutline />}
                  disabled={busy}
                  onClick={() =>
                    onBestaetigen({
                      date: preflight.heute,
                      source: slot.source,
                      time: slot.time,
                      is_today: true,
                      broadcast_source: slot.broadcast_source,
                      themen_anzahl: themenAnzahl,
                    })
                  }
                >
                  Jetzt bestätigen
                </Button>
              )}
              {absagbar && (
                <Button
                  size="small"
                  color="error"
                  disabled={busy}
                  onClick={() =>
                    onAbsagen({
                      date: preflight.heute,
                      source: slot.source,
                      time: slot.time,
                      is_today: true,
                      broadcast_source: slot.broadcast_source,
                      status: slot.status === 'pending' ? 'pending' : 'confirmed',
                    })
                  }
                >
                  Absagen …
                </Button>
              )}
              {bestaetigbar && (
                <Typography variant="caption" color="textSecondary" sx={{ flex: '1 1 240px' }}>
                  {themenAnzahl === 0
                    ? `${OHNE_THEMA_KEIN_BESTAETIGEN} `
                    : themenAnzahl === undefined
                      ? `${THEMEN_UNBEKANNT} `
                      : ''}
                  {fristText({ is_today: true })}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      );
    })}
  </Paper>
);

export default LiveAdmin;
